import { Link, useLocation } from 'react-router-dom'
import AuthButton from '../auth/AuthButton'
import NavMenu from './NavMenu'
import HeaderShell from './HeaderShell'
import ScanButton from './ScanButton'
import { scanCtaEnabled } from '../../lib/scanCta'
import SearchTrigger from '../search/SearchTrigger'
import SearchHost from '../search/SearchHost'
import { useCommonT } from '../../i18n'
import { ARIA_CURRENT_PAGE } from '../../lib/navV2.codes'

// Navigation v2's header (plan nav-search-2026-10-16, W1).
//
// Desktop: logo, Bracket / Build / Decks / Collection, the search trigger
// (Cmd-K), the purple Scan button (only with VITE_SCAN_CTA), the menu and
// the account.
// Phone, on Home / Collection / Decks: the top bar is hamburger + search
// field + account (the logo steps aside). Phone elsewhere (Bracket, Build and
// the rest keep their own page headers): logo, a search icon, menu, account.
//
// ONE NavMenu and ONE AuthButton across breakpoints, reordered with CSS
// `order` rather than duplicated (two instances would hold independent open
// state across a resize). HeaderShell keeps its h-14 box and gets only plain
// children; the search panel is portaled out by SearchHost, mounted beside
// the shell, not inside it.
const NAV_ITEMS = [
  { path: '/bracket', id: 'bracket' },
  { path: '/build', id: 'build' },
  { path: '/decks', id: 'decks' },
  { path: '/collection', id: 'collection' },
] as const

const TOP_BAR_PATHS = new Set(['/', '/collection', '/decks'])

function isActive(pathname: string, path: string): boolean {
  return pathname === path || (path === '/decks' && pathname.startsWith('/decks/'))
}

export default function HeaderV2() {
  const { pathname } = useLocation()
  const t = useCommonT()
  const topBar = TOP_BAR_PATHS.has(pathname)

  return (
    <>
      <HeaderShell>
        <div className="flex min-w-0 flex-1 items-center gap-3 md:gap-4">
          <Link
            to="/"
            className={`shrink-0 text-white font-bold text-lg tracking-tight transition-opacity hover:opacity-80 ${topBar ? 'hidden md:block' : ''}`}
          >
            {/* eslint-disable-next-line i18next/no-literal-string -- brand name */}
            {'CommanderBracket'}
          </Link>

          <nav aria-label={t('nav.primary_label')} className="hidden md:flex items-center gap-4 lg:gap-6">
            {NAV_ITEMS.map(({ path, id }) => {
              const active = isActive(pathname, path)
              return (
                <Link
                  key={path}
                  to={path}
                  aria-current={active ? ARIA_CURRENT_PAGE : undefined}
                  className={`text-sm font-medium transition-colors ${
                    active ? 'text-white' : 'text-gray-400 hover:text-gray-200'
                  }`}
                >
                  {t(`nav.${id}`)}
                </Link>
              )
            })}
          </nav>

          {/* Pushes the controls right everywhere except the phone top bar,
              where the search field takes the row. */}
          <div className={`flex-1 ${topBar ? 'hidden md:block' : ''}`} />

          <SearchTrigger field={topBar} />
          {/* Hidden until the iOS scanner ships (src/lib/scanCta.ts). */}
          {scanCtaEnabled() && <ScanButton />}
          {/* The hamburger leads the phone top bar (order-first). */}
          <div className={`flex items-center ${topBar ? 'order-first md:order-none' : ''}`}>
            <NavMenu />
          </div>
          <div className="flex shrink-0 items-center">
            <AuthButton />
          </div>
        </div>
      </HeaderShell>
      <SearchHost />
    </>
  )
}
